import { last, range, zip, uniq } from 'lodash';
import {
  GroupMap,
  GroupingResult,
  ParentGroupMap,
  PathObject,
} from '../../types';
import { isTruthy } from '@ardoq/common-helpers';
import {
  addGroupedComponentsAndProcessedReferences,
  getBaseGroup,
  getGroupIfIsComponentGroup,
  validatedParentLists,
} from './groupByReferenceTypes';
import {
  areSamePaths,
  pathStartsWithPath,
  removeParent,
  repeat,
  setParentsAndAddGroupIfNeeded,
  toPathObject,
} from './buildGroupsUtils';
import { GroupType, GroupingRule } from '@ardoq/api-types';
import { PathList } from './PathList';
import type { GraphInterface } from '../../graphInterface/graphInterface';
import { type I18nLocale } from '@ardoq/i18n';

export const insertParentGroups = (
  graphInterface: GraphInterface,
  ancestorPaths: string[][],
  groupBy: GroupingRule,
  index: number,
  result: GroupingResult,
  useNewGrouping: boolean,
  i18nLocale: I18nLocale
) => {
  /*
Groups are represented as paths, e.g [a, b, c] means a contains b contains c.

1. Consolidate ancestor paths, e.g. instead of processing
[a], [a, b], [a, b, c] only process [a, b, c].

2. Insert consolidated paths in existing pathes.
If the head of a consolidated path is in the ids of an existing path
build a new path.

3. Insert existing groups in new ancestor paths for each ancestor.
If the existing group is in the same base group and the existing group
is not in a reference type group or is in the same reference type group
as the ancestor group, build a new group.
*/

  const consolidatedAncestorPaths =
    consolidateAndSortAncestorPaths(ancestorPaths);

  const error = validatedParentLists(
    graphInterface,
    consolidatedAncestorPaths,
    result.groupedComponents,
    result.parentGroupMap,
    result.groupMapRaw,
    groupBy,
    index,
    useNewGrouping,
    i18nLocale
  );

  if (error) {
    result.errors.push(error);
    return error;
  }

  const { groupMapRaw, parentGroupMap } = result;

  addGroupedComponentsAndProcessedReferences(consolidatedAncestorPaths, result);

  const withAncestorGroups = consolidatedAncestorPaths.flatMap(ancestorPath =>
    createNewAncestorGroups(
      graphInterface,
      result.pathList,
      ancestorPath,
      groupBy.type,
      groupBy.sublabel,
      groupMapRaw,
      parentGroupMap
    )
  );

  const { newPathObjects, processed } = withAncestorGroups.reduce(
    (
      { newPathObjects, processed },
      { pathObject: targetPathObject, index, baseGroup, referenceGroup }
    ) =>
      range(index + 1, targetPathObject.path.length).reduce(
        ({ newPathObjects, processed }, childIndex) =>
          result.pathList
            .filter(candidatePathObject =>
              canBeInserted(
                graphInterface,
                candidatePathObject,
                targetPathObject,
                childIndex,
                baseGroup,
                referenceGroup
              )
            )
            .reduce(
              ({ newPathObjects, processed }, pathObject) => {
                processed.add(pathObject);
                const headIndex = pathObject.path.indexOf(
                  targetPathObject.path[childIndex]
                );
                removeParent(
                  pathObject.path[headIndex],
                  pathObject.path[headIndex - 1]!,
                  result.parentGroupMap
                );
                // The new parent is already set.
                const tail = pathObject.path.slice(headIndex);
                return tail.length === 1
                  ? { newPathObjects, processed }
                  : {
                      newPathObjects: [
                        ...newPathObjects,
                        toPathObject(
                          graphInterface,
                          [
                            ...targetPathObject.path.slice(0, childIndex),
                            ...tail,
                          ],
                          [
                            ...targetPathObject.sublabels.slice(0, childIndex),
                            ...pathObject.sublabels.slice(headIndex),
                          ]
                        ),
                      ],
                      processed,
                    };
              },
              { newPathObjects, processed }
            ),
        { newPathObjects, processed }
      ),
    { newPathObjects: [] as PathObject[], processed: new Set<PathObject>() }
  );
  const pathObjects = mergeOverlappingPaths(
    graphInterface,
    consolidate([
      ...result.pathList.filter(pathObject => !processed.has(pathObject)),
      ...newPathObjects,
      ...withAncestorGroups.map(({ pathObject }) => pathObject),
    ])
  );

  result.pathList = new PathList();
  result.pathList.addPaths(pathObjects);
};

const canBeInserted = (
  graphInterface: GraphInterface,
  candidatePathObject: PathObject,
  targetPathObject: PathObject,
  index: number,
  baseGroup: string | null,
  referenceGroup: string | null
) => {
  const head = targetPathObject.path[index];

  if (!candidatePathObject.ids.has(head)) {
    return false;
  }

  const headIndex = candidatePathObject.path.indexOf(head);
  const candidateComponentGroup = getGroupIfIsComponentGroup(
    graphInterface,
    candidatePathObject.path[headIndex - 1]
  );

  return (
    getBaseGroup(graphInterface, headIndex, candidatePathObject.path) ===
      baseGroup &&
    (candidateComponentGroup === null ||
      candidateComponentGroup === referenceGroup ||
      // The candidate component group is already the component group
      // in the new path, e.g. if a reference type group also contains
      // actual children of the grouping component and we are now grouping
      // by parent all.
      candidateComponentGroup === targetPathObject.path[index - 1])
  );
};

const consolidate = (pathObjectList: PathObject[]) =>
  pathObjectList.filter(
    pathObjectA =>
      !pathObjectList.some(
        pathObjectB =>
          pathObjectA !== pathObjectB &&
          pathStartsWithPath(pathObjectB.path, pathObjectA.path)
      )
  );

const consolidateAndSortAncestorPaths = (paths: string[][]) =>
  paths
    .sort((a, b) => b.length - a.length)
    .filter(
      cand =>
        !paths.some(
          path =>
            path !== cand &&
            path.length > cand.length &&
            pathStartsWithPath(path, cand)
        )
    );

type WithAncestorGroup = {
  pathObject: PathObject;
  index: number;
  baseGroup: string | null;
  referenceGroup: string | null;
};

const createNewAncestorGroups = (
  graphInterface: GraphInterface,
  pathObjects: PathObject[],
  ancestorPath: string[],
  type: GroupType,
  sublabel = '',
  groupMapRaw: GroupMap,
  parentGroupMap: ParentGroupMap
) => {
  const head = ancestorPath[0];

  const newPathObjects = pathObjects.reduce<WithAncestorGroup[]>(
    (newPathObjects, pathObject) => {
      if (!pathObject.ids.has(head)) {
        return newPathObjects;
      }

      const { path: existingPath, sublabels: existingSublabels } = pathObject;
      const index = existingPath.indexOf(head);
      const existingAncestorGroups = existingPath.slice(0, index);
      const currentParentGroupId = last(existingAncestorGroups) || null;

      setParentsAndAddGroupIfNeeded(
        graphInterface,
        ancestorPath,
        currentParentGroupId,
        type,
        sublabel,
        groupMapRaw,
        parentGroupMap
      );

      const newPath = [...existingAncestorGroups, ...ancestorPath];
      if (
        newPathObjects.some(({ pathObject: { path } }) =>
          areSamePaths(path, newPath)
        )
      ) {
        return newPathObjects;
      }

      const newSublabels = zip(
        existingSublabels.slice(index, index + ancestorPath.length),
        [...repeat([sublabel], ancestorPath.length - 1), []]
      ).map(([sublabelsA = [], sublabelsB = []]) => [
        ...sublabelsA,
        ...sublabelsB,
      ]);

      return [
        ...newPathObjects,
        {
          pathObject: toPathObject(graphInterface, newPath, [
            ...existingSublabels.slice(0, index),
            ...newSublabels,
          ]),
          index,
          baseGroup: getBaseGroup(
            graphInterface,
            existingAncestorGroups.length,
            newPath
          ),
          referenceGroup: getGroupIfIsComponentGroup(
            graphInterface,
            last(existingAncestorGroups)
          ),
        },
      ];
    },
    []
  );

  if (newPathObjects.length === 0) {
    setParentsAndAddGroupIfNeeded(
      graphInterface,
      ancestorPath,
      null,
      type,
      sublabel,
      groupMapRaw,
      parentGroupMap
    );
  }

  return newPathObjects.length > 0
    ? newPathObjects
    : {
        pathObject: toPathObject(graphInterface, ancestorPath, [
          ...repeat([sublabel], ancestorPath.length - 1),
          [],
        ]),
        index: 0,
        baseGroup: null,
        referenceGroup: null,
      };
};

type MergeTarget = {
  head: PathObject;
  tail: PathObject;
  startIndex: number;
};

const mergeOverlappingPaths = (
  graphInterface: GraphInterface,
  pathObjects: PathObject[]
) => {
  const mergeTargets = getMergeTargets(pathObjects);

  const mergedPathsObjects = mergeTargets.map(({ head, tail, startIndex }) => {
    const tailStartIndex = head.path.length - startIndex;
    return toPathObject(
      graphInterface,
      [...head.path, ...tail.path.slice(tailStartIndex)],
      [
        ...head.sublabels.slice(0, startIndex),
        ...zip(
          head.sublabels.slice(startIndex),
          tail.sublabels.slice(0, tailStartIndex)
        ).map(([headSublabels, tailSublabels]) =>
          uniq(
            [...(headSublabels || []), ...(tailSublabels || [])].filter(
              isTruthy
            )
          )
        ),
        ...tail.sublabels.slice(tailStartIndex),
      ]
    );
  });

  const mergedPathsObjectsSet = new Set(
    mergeTargets.flatMap(({ head, tail }) => [head, tail])
  );

  return [
    ...pathObjects.filter(pathObject => !mergedPathsObjectsSet.has(pathObject)),
    ...mergedPathsObjects,
  ];
};

const getMergeTargets = (pathObjects: PathObject[]) => {
  const mergeTargets: MergeTarget[] = [];
  for (const [pathObjectA, pathObjectB] of getCombinations(pathObjects)) {
    const { path: pathA } = pathObjectA;
    const { path: pathB, ids: idsB } = pathObjectB;
    const lastA = last(pathA)!;
    const firstA = pathA[0];
    if (idsB.has(lastA)) {
      const startIndex = pathB.indexOf(lastA);
      if (arePathsOverlapping(pathA, pathB, pathA.length - 1 - startIndex)) {
        mergeTargets.push({
          head: pathObjectA,
          tail: pathObjectB,
          startIndex,
        });
      }
    } else if (idsB.has(firstA)) {
      const startIndex = pathB.indexOf(firstA);
      if (arePathsOverlapping(pathB, pathA, startIndex)) {
        mergeTargets.push({
          head: pathObjectB,
          tail: pathObjectA,
          startIndex,
        });
      }
    }
  }
  return mergeTargets;
};

function* getCombinations<T>(list: T[]) {
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      yield [list[i], list[j]];
    }
  }
}

const arePathsOverlapping = (
  pathA: string[],
  pathB: string[],
  startIndex: number
) =>
  pathB.every(
    (idB, index) =>
      pathA[index + startIndex] === idB ||
      pathA[index + startIndex] === undefined
  );
